/**
 * CommissionTableRow Component
 * 수수료 테이블 행 컴포넌트
 * - 현재 적용 중인 수수료 표시
 * - 수수료변경 버튼 클릭 시 새 수수료 입력 행 표시
 */
import React from 'react';
import DateInput from '@/shared/components/forms/DateInput';
import { formatCurrency } from '@/shared/utils/formatter';
import { SupplierCommissionRow, PeriodType } from '../types/feeManagement.types';
import { COMMISSION_TYPE_OPTIONS, getAmountUnit, getTodayString } from '../lib/feeManagement.helpers';

interface CommissionTableRowProps {
    /** 행 데이터 */
    row: SupplierCommissionRow;
    /** 테이블 인덱스 */
    index: number;
    /** 새 수수료 구분 변경 핸들러 */
    onNewCommissionTypeChange: (supplierId: string, period: PeriodType, value: string) => void;
    /** 새 금액 변경 핸들러 */
    onNewAmountChange: (supplierId: string, period: PeriodType, value: string) => void;
    /** 페이백 사용여부 변경 핸들러 */
    onNewPaybackUseYnChange: (supplierId: string, value: string) => void;
    /** 페이백 금액 변경 핸들러 */
    onNewPaybackAmountChange: (supplierId: string, value: string) => void;
    /** 새 시작일자 변경 핸들러 */
    onNewStartDateChange: (supplierId: string, value: string) => void;
    /** 히스토리 조회 핸들러 */
    onShowHistory: (supplierId: string, supplierName: string) => void;
    /** 저장 핸들러 */
    onSave: (supplierId: string) => void;
    /** 수수료변경 토글 핸들러 */
    onToggleFeeChange: (supplierId: string) => void;
}

/** 수수료 구분 코드를 한글 레이블로 변환 */
const getCommissionTypeLabel = (type: string): string => {
    const option = COMMISSION_TYPE_OPTIONS.find(opt => opt.value === type);
    return option ? option.label : '-';
};

/** 금액 포맷팅 (단위 포함) */
const formatAmountWithUnit = (amount: string, type: string): string => {
    if (!amount || !type) return '-';
    const unit = getAmountUnit(type);
    return `${formatCurrency(amount)}${unit}`;
};

const CommissionTableRow: React.FC<CommissionTableRowProps> = ({
    row,
    index,
    onNewCommissionTypeChange,
    onNewAmountChange,
    onNewPaybackUseYnChange,
    onNewPaybackAmountChange,
    onNewStartDateChange,
    onShowHistory,
    onSave,
    onToggleFeeChange
}) => {
    /** 새 금액 입력 핸들러 - 숫자만 허용, 정률 타입은 100% 제한 */
    const handleNewAmountInput = (period: PeriodType, value: string) => {
        const numericValue = value.replace(/[^0-9]/g, '');

        // 정률 타입일 때 100% 제한
        const commissionType = period === 'daily' ? row.newDailyCommissionType : row.newWeeklyCommissionType;
        if (commissionType?.includes('RATE')) {
            const numValue = parseInt(numericValue) || 0;
            if (numValue > 100) {
                return;
            }
        }

        onNewAmountChange(row.supplierId, period, numericValue);
    };

    return (
        <>
            {/* 현재 적용 중인 수수료 행 */}
            <tr>
                {/* NO */}
                <td className="text-center" rowSpan={row.isAddingNew ? 2 : 1}>
                    {index}
                </td>

                {/* 협력사명 */}
                <td rowSpan={row.isAddingNew ? 2 : 1}>{row.supplierName}</td>

                {/* 선정산 (수수료구분 + 금액) */}
                <td className="text-center">
                    {row.dailyCommissionType ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            <div style={{ fontSize: '0.7rem', color: '#059669', fontWeight: 'bold' }}>
                                현재 적용 중
                            </div>
                            <div style={{ fontSize: '0.85rem' }}>
                                {getCommissionTypeLabel(row.dailyCommissionType)}
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                                {formatAmountWithUnit(row.dailyAmount, row.dailyCommissionType)}
                            </div>
                        </div>
                    ) : (
                        <span>-</span>
                    )}
                </td>

                {/* 주정산 (수수료구분 + 금액) */}
                <td className="text-center">
                    {row.weeklyCommissionType ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            <div style={{ fontSize: '0.7rem', color: '#059669', fontWeight: 'bold' }}>
                                현재 적용 중
                            </div>
                            <div style={{ fontSize: '0.85rem' }}>
                                {getCommissionTypeLabel(row.weeklyCommissionType)}
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                                {formatAmountWithUnit(row.weeklyAmount, row.weeklyCommissionType)}
                            </div>
                        </div>
                    ) : (
                        <span>-</span>
                    )}
                </td>

                {/* 페이백(건당) */}
                <td className="text-center">
                    {row.weeklyPaybackUseYn === 'Y' && row.weeklyPaybackAmount ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            <div style={{ fontSize: '0.7rem', color: '#059669', fontWeight: 'bold' }}>
                                사용중
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: '600'}}>
                                {formatCurrency(row.weeklyPaybackAmount)}원
                            </div>
                        </div>
                    ) : row.scheduledPaybackUseYn === 'Y' && row.scheduledPaybackAmount ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                            <div style={{ fontSize: '0.7rem', color: '#2563eb', fontWeight: 'bold' }}>
                                {row.scheduledPaybackApplyDate} 적용 예정
                            </div>
                            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#2563eb' }}>
                                {formatCurrency(row.scheduledPaybackAmount)}원
                            </div>
                        </div>
                    ) : (
                        <span style={{ color: '#9ca3af' }}>미사용</span>
                    )}
                </td>

                {/* 적용일 */}
                <td className="text-center">
                    {row.startDate || '-'}
                </td>

                {/* 수수료변경 히스토리 */}
                <td className="text-center" rowSpan={row.isAddingNew ? 2 : 1}>
                    <button
                        className="btn btn-secondary"
                        style={{ fontSize: '0.85rem', padding: '5px 10px' }}
                        onClick={() => onShowHistory(row.supplierId, row.supplierName)}
                    >
                        <i className="fas fa-history"></i>
                        <span style={{ marginLeft: '4px' }}>이력</span>
                    </button>
                </td>

                {/* 저장 - 새 수수료 입력 행이 있을 때만 활성화 */}
                <td className="text-center" rowSpan={row.isAddingNew ? 2 : 1}>
                    <button
                        className="btn btn-primary"
                        style={{ fontSize: '0.85rem', padding: '5px 10px' }}
                        onClick={() => onSave(row.supplierId)}
                        disabled={!row.isAddingNew || !row.isEdited}
                    >
                        <i className="fas fa-save"></i>
                        <span style={{ marginLeft: '4px' }}>저장</span>
                    </button>
                </td>

                {/* 수수료변경 버튼 */}
                <td className="text-center" rowSpan={row.isAddingNew ? 2 : 1}>
                    <button
                        className={`btn ${row.isAddingNew ? 'btn-secondary' : 'btn-primary'}`}
                        style={{ fontSize: '0.85rem', padding: '5px 10px' }}
                        onClick={() => onToggleFeeChange(row.supplierId)}
                    >
                        <i className={`fas ${row.isAddingNew ? 'fa-times' : 'fa-edit'}`}></i>
                        <span style={{ marginLeft: '4px' }}>{row.isAddingNew ? '취소' : '변경'}</span>
                    </button>
                </td>
            </tr>

            {/* 새 수수료 입력 행 (수수료변경 클릭 시 표시) */}
            {row.isAddingNew && (
                <tr style={{ backgroundColor: '#f0f9ff' }}>
                    {/* 선정산 (수수료구분 + 금액 입력) */}
                    <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '4px' }}>
                            <div style={{ fontSize: '0.7rem', color: '#2563eb', fontWeight: 'bold' }}>
                                변경할 수수료
                            </div>
                            <select
                                className="filter-select"
                                style={{ width: '100%', fontSize: '0.8rem' }}
                                value={row.newDailyCommissionType || ''}
                                onChange={(e) => onNewCommissionTypeChange(row.supplierId, 'daily', e.target.value)}
                            >
                                {COMMISSION_TYPE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                <input
                                    type="text"
                                    className="filter-input"
                                    style={{ textAlign: 'right', fontSize: '0.85rem' }}
                                    value={row.newDailyAmount ? formatCurrency(row.newDailyAmount) : ''}
                                    onChange={(e) => handleNewAmountInput('daily', e.target.value)}
                                    placeholder="금액"
                                    disabled={!row.newDailyCommissionType}
                                />
                                <span style={{ fontSize: '0.85rem' }}>
                                    {row.newDailyCommissionType ? getAmountUnit(row.newDailyCommissionType) : ''}
                                </span>
                            </div>
                        </div>
                    </td>

                    {/* 주정산 (수수료구분 + 금액 입력) */}
                    <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '4px' }}>
                            <div style={{ fontSize: '0.7rem', color: '#2563eb', fontWeight: 'bold' }}>
                                변경할 수수료
                            </div>
                            <select
                                className="filter-select"
                                style={{ width: '100%', fontSize: '0.8rem' }}
                                value={row.newWeeklyCommissionType || ''}
                                onChange={(e) => onNewCommissionTypeChange(row.supplierId, 'weekly', e.target.value)}
                            >
                                {COMMISSION_TYPE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                <input
                                    type="text"
                                    className="filter-input"
                                    style={{ textAlign: 'right', fontSize: '0.85rem' }}
                                    value={row.newWeeklyAmount ? formatCurrency(row.newWeeklyAmount) : ''}
                                    onChange={(e) => handleNewAmountInput('weekly', e.target.value)}
                                    placeholder="금액"
                                    disabled={!row.newWeeklyCommissionType}
                                />
                                <span style={{ fontSize: '0.85rem' }}>
                                    {row.newWeeklyCommissionType ? getAmountUnit(row.newWeeklyCommissionType) : ''}
                                </span>
                            </div>
                        </div>
                    </td>

                    {/* 페이백(건당) 입력 */}
                    <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', padding: '4px' }}>
                            <div style={{ fontSize: '0.7rem', color: '#2563eb', fontWeight: 'bold' }}>
                                페이백 설정
                            </div>
                            <select
                                className="filter-select"
                                style={{ width: '100%', fontSize: '0.8rem' }}
                                value={row.newWeeklyPaybackUseYn || 'N'}
                                onChange={(e) => onNewPaybackUseYnChange(row.supplierId, e.target.value)}
                            >
                                <option value="N">미사용</option>
                                <option value="Y">사용</option>
                            </select>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                                <input
                                    type="text"
                                    className="filter-input"
                                    style={{ textAlign: 'right', fontSize: '0.85rem' }}
                                    value={row.newWeeklyPaybackAmount ? formatCurrency(row.newWeeklyPaybackAmount) : ''}
                                    onChange={(e) => {
                                        const numericValue = e.target.value.replace(/[^0-9]/g, '');
                                        onNewPaybackAmountChange(row.supplierId, numericValue);
                                    }}
                                    placeholder="금액"
                                    disabled={row.newWeeklyPaybackUseYn !== 'Y'}
                                />
                            </div>
                        </div>
                    </td>

                    {/* 새 적용일 (입력) */}
                    <td className="text-center">
                        <div style={{ padding: '4px' }}>
                            <DateInput
                                id={`newStartDate-${row.supplierId}`}
                                value={row.newStartDate || ''}
                                onChange={(value) => onNewStartDateChange(row.supplierId, value)}
                                min={getTodayString()}
                                className="form-input"
                            />
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default CommissionTableRow;
